import React, { FC, useState } from "react";
import { Icon } from "../../../components/Component";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { Form, Input, Modal, Table, Tag, Button, Space } from "antd";
import { tasksApis } from "../../../api/NewTasks";
import TaskCheckListCreateModal from "./TaskCheckListCreateModal";
import TaskCheckListViewAndUpdateModal from "./TaskCheckListViewAndUpdateModal";
import Swal from "sweetalert2";
import CheckListDrawer from "./CheckListDrawer";
import type { RowData } from "./CheckListDrawer";
import { workApprovalApis } from "../../../api/WorkApproval";
import { ColumnType } from "antd/es/table";
import { useSelector } from "react-redux";
import {
  AddAccess,
  EditAccess,
  CancelAccess,
} from "../../../components/accessControl/AccessControl";

export interface TaskTagsInterface {
  tagId: number;
  tagName: string;
  taskTagId: number;
}

export type TaskFlagEnum = "Internal" | "External";

interface ChecklistLink {
  checklistLinkId: number;
  checklistId: number;
  checklistName: string;
  isMandatory: boolean;
  answered: boolean;
  status: string;
  activeStatus: string;
  approvalStatus?: "Pending" | "Approved" | "Rejected";
  stateApprovalId?: number;
  approvalLinkId?: string;
  stateApprovals?: Array<{
    stateApprovalId: number;
    approvalLinkId: string;
    status: string;
    approvalStatus?: string;
  }>;
  rawData?: any;
}

interface TaskCheckListViewProps {
  checklistResponses: ChecklistLink[];
  task: any;
  refreshData: () => void;
  isFromWorkUI?: boolean;
}

const TaskCheckListView: FC<TaskCheckListViewProps> = ({
  checklistResponses,
  task,
  refreshData,
  isFromWorkUI,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChecklistId, setSelectedChecklistId] = useState<number | null>(
    null
  );
  const [showChecklistViewAndUpdate, setShowChecklistViewAndUpdate] =
    useState(false);
  const [addChecklistDrawerOpen, setAddChecklistDrawerOpen] = useState(false);

  const [currentRejectItem, setCurrentRejectItem] =
    useState<ChecklistLink | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);

  const [drawerInitialEditMode, setDrawerInitialEditMode] = useState(false);

  const handleView = (record: ChecklistLink) => {
    setSelectedChecklistId(record.checklistId);
    setDrawerInitialEditMode(!record.answered && !isFromWorkUI);
    setShowChecklistViewAndUpdate(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedChecklistId(null);
  };

  // Delete handler
  const handleDelete = (record: ChecklistLink) => {
    if (isFromWorkUI) return;

    if (!record?.checklistLinkId) {
      Swal.fire(
        "Error",
        "Cannot delete: checklist link id is missing.",
        "error"
      );
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this checklist?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const payload = {
        taskId: task.taskId,
        projectId: task.projectId,
        phaseId: task.phaseId,
        activityId: task.activityId,
        checklistRequired: true,
        checklistLinks: {
          deletedLines: [{ checklistLinkId: record.checklistLinkId }],
        },
      };

      try {
        const response: any = await tasksApis.updateTaskbyId(payload);
        const { status, message, error, data } = response || {};

        // The previous code always showed success even when the API
        // returned { status: false, ... }, so the user thought delete
        // didn't work. Trust the response status and show the server's
        // own error message when it fails.
        if (!status) {
          Swal.fire(
            "Error",
            error || message || data?.message || "Failed to delete checklist.",
            "error"
          );
          return;
        }

        Swal.fire("Deleted!", "Checklist has been deleted.", "success");

        try {
          await refreshData();
        } catch (refreshErr) {
          Swal.fire(
            "Warning",
            "Checklist deleted, but failed to refresh data.",
            "warning"
          );
        }
      } catch (e: any) {
        const apiMessage =
          e?.response?.data?.message || e?.message || "Failed to delete checklist.";
        Swal.fire("Error", apiMessage, "error");
      }
    });
  };

  const handleAddChecklist = () => {
    if (isFromWorkUI) return;
    setAddChecklistDrawerOpen(true);
  };

  const handleAddChecklistDrawerClose = () => {
    setAddChecklistDrawerOpen(false);
  };

  const handleAddChecklistSubmit = async (rows: RowData[]) => {
    if (!rows || !rows.length) return;

    const validRows = rows.filter(
      (row: RowData) => row.status && row.checklist
    );

    if (validRows.length === 0) {
      console.warn("No valid checklist data to add");
      setAddChecklistDrawerOpen(false);
      return;
    }

    const newLines = validRows
      .filter(
        (row: RowData) =>
          row.checklist && !associatedChecklistIds.includes(row.checklist)
      )
      .map((row: RowData) => ({
        checklistId: row.checklist,
        status: row.status,
        isMandatory: row.mandatory,
      }));
    if (!newLines.length) {
      setAddChecklistDrawerOpen(false);
      return;
    }
    const payload = {
      taskId: task.taskId,
      projectId: task.projectId,
      phaseId: task.phaseId,
      activityId: task.activityId,
      checklistRequired: true,
      checklistLinks: {
        newLines,
      },
    };
    try {
      await tasksApis.updateTaskbyId(payload);
      Swal.fire({
        icon: "success",
        title: "Checklist Added",
        text: "Checklist has been added successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
      try {
        await refreshData();
      } catch (refreshErr) {
        Swal.fire(
          "Warning",
          "Checklist added, but failed to refresh data.",
          "warning"
        );
      }
    } catch (e) {
      Swal.fire("Error", "Failed to add checklist.", "error");
    } finally {
      setAddChecklistDrawerOpen(false);
    }
  };

  const associatedChecklistIds = checklistResponses.map((c) => c.checklistId);

  const statusOrder = ["Open", "OnHold", "InProgress", "UnderQC", "Closed"];
  const currentStatusIndex = statusOrder.findIndex(
    (s) => s.toLowerCase() === (task?.taskStatus || "").toLowerCase()
  );

  const handleApprove = async (record: ChecklistLink) => {
    try {
      const stateApproval = record.stateApprovals?.[0];
      if (!stateApproval) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "Approval data not found",
          confirmButtonText: "OK",
        });
        return;
      }

      const allVerificationsCompleted = record?.stateApprovals?.every(
        (approval: any) => {
          return (
            approval.approvalStatus === "Approved" ||
            approval.stateApprovalId === stateApproval.stateApprovalId
          );
        }
      );

      if (!allVerificationsCompleted) {
        await Swal.fire({
          icon: "error",
          title: "Cannot Approve",
          text: "Action cannot be performed until all Checker/Verifier approvals are completed.",
          confirmButtonText: "OK",
        });
        return;
      }

      const response = await workApprovalApis.approveState({
        stateApprovalId: stateApproval.stateApprovalId,
      });

      const { status, message } = response;

      if (status) {
        await Swal.fire({
          icon: "success",
          title: "Approved",
          text: "Checklist approved successfully",
          confirmButtonText: "OK",
        });

        refreshData();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: message,
          confirmButtonText: "OK",
        });
      }
    } catch (error: any) {
      console.error("Error approving:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to approve checklist",
        confirmButtonText: "OK",
      });
    }
  };

  const handleRejectClick = (record: ChecklistLink) => {
    setCurrentRejectItem(record);
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      if (currentRejectItem) {
        const stateApproval = currentRejectItem.stateApprovals?.[0];
        if (!stateApproval) {
          await Swal.fire({
            icon: "error",
            title: "Error",
            text: "Approval data not found",
            confirmButtonText: "OK",
          });
          return;
        }

        const allVerificationsCompleted =
          currentRejectItem.stateApprovals?.every(
            (approval: any) =>
              approval.approvalStatus === "Approved" ||
              approval.stateApprovalId === stateApproval.stateApprovalId
          );

        if (!allVerificationsCompleted) {
          await Swal.fire({
            icon: "error",
            title: "Cannot Reject",
            text: "Action cannot be performed until all Checker/Verifier approvals are completed.",
            confirmButtonText: "OK",
          });
          return;
        }

        const response = await workApprovalApis.rejectState({
          stateApprovalId: stateApproval.stateApprovalId,
          reason,
        });

        const { status, message } = response;
        if (!status) {
          await Swal.fire({
            icon: "error",
            title: "Error",
            text: message,
            confirmButtonText: "OK",
          });
        } else if (status) {
          await Swal.fire({
            icon: "success",
            title: "Rejected",
            text: "Checklist rejected successfully",
            confirmButtonText: "OK",
          });

          refreshData();
        }
      }
    } catch (error: any) {
      console.error("Error rejecting:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to reject checklist",
        confirmButtonText: "OK",
      });
    } finally {
      setIsRejecting(false);
      setRejectModalVisible(false);
      setCurrentRejectItem(null);
    }
  };

  const RejectModal = ({
    isModalVisible,
    setIsModalVisible,
    onReject,
    title = "Reject Checklist",
    label = "Reason for rejection",
    isLoading = false,
  }: {
    isModalVisible: boolean;
    setIsModalVisible: (visible: boolean) => void;
    onReject: (reason: string) => void;
    title?: string;
    label?: string;
    isLoading?: boolean;
  }) => {
    const [form] = Form.useForm();

    const handleCancel = () => {
      setIsModalVisible(false);
    };

    const handleOk = () => {
      form
        .validateFields()
        .then((values) => {
          const reason = values.reason;
          onReject(reason);
          form.resetFields();
          setIsModalVisible(false);
        })
        .catch((info) => {
          console.log("Validate Failed:", info);
        });
    };

    return (
      <Modal
        title={title}
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label={label}
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea placeholder="Type your reason here..." />
          </Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              onClick={handleOk}
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </Button>
            <Button onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
          </Space>
        </Form>
      </Modal>
    );
  };

  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const isAdmin =
    (currentUser?.genericRole || "").toLowerCase() === "admin";

  // FIX 1: Improved approver check to properly identify if user is an approver
  // Admin override: admins can approve any checklist with a Pending entry.
  const isUserApproverForChecklist = (checklist: ChecklistLink) => {
    if (!checklist?.stateApprovals?.length) return false;
    if (
      checklist.stateApprovals.some(
        (approval: any) =>
          approval.userId === currentUser?.id &&
          (approval.status === "Pending" ||
            approval.approvalStatus === "Pending")
      )
    )
      return true;
    if (
      isAdmin &&
      checklist.stateApprovals.some(
        (approval: any) =>
          approval.status === "Pending" ||
          approval.approvalStatus === "Pending"
      )
    )
      return true;
    return false;
  };

  // FIX 2: Check if there are any checklists with approval data (not just where user is approver)
  const hasApprovalData = checklistResponses.some(
    checklist => checklist.stateApprovals && checklist.stateApprovals.length > 0
  );

  // Check if there are any checklists where current user is an approver
  const hasApproverChecklists = checklistResponses.some(
    isUserApproverForChecklist
  );

  const columns: ColumnType<ChecklistLink>[] = [
    {
      title: "Checklist Title",
      dataIndex: "checklistName",
      key: "checklistName",
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
    },
    {
      title: "Mandatory",
      dataIndex: "isMandatory",
      key: "isMandatory",
      width: 100,
      render: (val: boolean) => (val ? "Yes" : "No"),
    },
    {
      title: "Responded",
      dataIndex: "answered",
      key: "answered",
      width: 100,
      render: (_: boolean, record: ChecklistLink) => {
        return record.answered ? "Yes" : "No";
      },
    },
    // FIX 2: Always show Approval Status column if there's approval data
    ...(hasApprovalData
      ? [
          {
            title: "Approval Status",
            dataIndex: "stateApprovals",
            key: "approvalStatus",
            width: 150,
            render: (
              approvals: Array<{ status: string; approvalStatus?: string }> = [],
              record: ChecklistLink
            ) => {
              if (!approvals || approvals.length === 0) return "NA";
              
              // Use approvalStatus if available, otherwise fall back to status
              const status = approvals[0]?.approvalStatus || approvals[0]?.status || "NA";
              let color = "orange";
              if (status === "Approved") color = "#38d39f";
              else if (status === "Rejected") color = "#e74c3c";
              else if (status === "NA") return "NA";
              
              return <Tag color={color}>{status}</Tag>;
            },
          },
        ]
      : []),
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: ChecklistLink) => {
        if (isFromWorkUI) {
          // FIX 1: Only show view button for responded checklists in work UI
          if (!record.answered) return null;
          
          return (
            <span
              style={{
                cursor: "pointer",
                color: "#4169E1",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
              }}
              title="View"
              onClick={() => handleView(record)}
            >
              <Icon name="eye" />
            </span>
          );
        }
        
        const checklistStatusIndex = statusOrder.findIndex(
          (s) => s.toLowerCase() === (record.status || "").toLowerCase()
        );

        // Only allow Fill for current and previous statuses
        if (
          !record.answered &&
          checklistStatusIndex !== -1 &&
          checklistStatusIndex <= currentStatusIndex
        ) {
          return (
            <EditAccess moduleName="Tasks">
              <span
                style={{
                  cursor: "pointer",
                  color: "#4169E1",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                }}
                title="Fill"
                onClick={() => handleView(record)}
              >
                <Icon name="edit" />
              </span>
            </EditAccess>
          );
        }
        // Show View only for answered checklists (FIX 1)
        if (record.answered) {
          return (
            <span
              style={{
                cursor: "pointer",
                color: "#4169E1",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
              }}
              title="View"
              onClick={() => handleView(record)}
            >
              <Icon name="eye" />
            </span>
          );
        }
        // Show Delete for not answered and not eligible for Fill
        if (
          !record.answered &&
          (checklistStatusIndex === -1 ||
            checklistStatusIndex > currentStatusIndex)
        ) {
          return (
            <CancelAccess moduleName="Tasks">
              <span
                style={{
                  cursor: "pointer",
                  color: "#ff4d4f",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                }}
                title="Delete"
                onClick={() => handleDelete(record)}
              >
                <Icon name="trash" />
              </span>
            </CancelAccess>
          );
        }
        return null;
      },
    },
    // FIX 2: Show Approval Action column if there's approval data
    ...(hasApproverChecklists
      ? [
          {
            title: "Approval Action",
            key: "approvalAction",
            width: 120,
            render: (_: any, record: ChecklistLink) => {
              if (!isUserApproverForChecklist(record)) return null;

              const hasPendingApproval = record.stateApprovals?.some(
                (a) => a.status === "Pending" || a.approvalStatus === "Pending"
              );

              return (
                <div className="d-flex align-items-center">
                  <UncontrolledDropdown direction="start">
                    <DropdownToggle
                      tag="a"
                      className="no-style-toggle"
                      style={{
                        padding: "4px",
                        fontSize: "16px",
                        color: "#555",
                      }}
                    >
                      <Icon name="more-h" />
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem
                        onClick={() => handleApprove(record)}
                        disabled={!hasPendingApproval}
                      >
                        <Icon name="check" className="me-1" />
                        Approve
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => handleRejectClick(record)}
                        disabled={!hasPendingApproval}
                      >
                        <Icon name="cross" className="me-1" />
                        Reject
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <div
        className="card-inner card-inner-lg p-0"
        style={{ padding: 0, margin: 0 }}
      >
        <div className="content clearfix" style={{ padding: "5px" }}>
          {!isFromWorkUI && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                margin: "2px 0 8px 0",
              }}
            >
              <ul className="nk-ibx-head-tools g-1 m-0 p-0">
                <li className="pt-0 pb-0">
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleAddChecklist}
                  >
                    <Icon name={"plus"} />
                    <span>Add Checklist </span>
                  </Button>
                </li>
              </ul>
            </div>
          )}
          <div style={{ margin: "8px auto 0 auto" }}>
            <Table
              rowKey="checklistLinkId"
              columns={columns}
              dataSource={checklistResponses}
              pagination={false}
              bordered
              size="middle"
              scroll={{ x: 1100 }}
              style={{ margin: 0 }}
            />
          </div>
        </div>
      </div>

      {!isFromWorkUI && (
        <>
          <CheckListDrawer
            key={addChecklistDrawerOpen ? 1 : 0}
            open={addChecklistDrawerOpen}
            onClose={handleAddChecklistDrawerClose}
            onSubmit={handleAddChecklistSubmit}
            setChecklistSubmissionStatus={() => {}}
            ChecklistData={[]}
            siteId={task?.siteId ?? 0}
            associatedChecklistIds={associatedChecklistIds}
            associatedStatuses={checklistResponses.map((c) => c.status)}
          />
          <TaskCheckListCreateModal
            open={modalOpen}
            checklistId={selectedChecklistId}
            checklistLinkId={
              selectedChecklistId
                ? checklistResponses.find(
                    (c) => c.checklistId === selectedChecklistId
                  )?.checklistLinkId
                : null
            }
            onClose={handleModalClose}
            onSave={(success: boolean) => {
              if (success) {
                Swal.fire({
                  icon: "success",
                  title: "Checklist Submitted",
                  text: "Checklist has been filled successfully!",
                  timer: 2000,
                  showConfirmButton: false,
                });
                refreshData();
              }
              handleModalClose();
            }}
          />
        </>
      )}

      <TaskCheckListViewAndUpdateModal
        open={showChecklistViewAndUpdate}
        checklistId={selectedChecklistId}
        isFromWorkUI={isFromWorkUI}
        initialEditMode={drawerInitialEditMode}
        checklistLinkId={
          selectedChecklistId
            ? checklistResponses.find(
                (c) => c.checklistId === selectedChecklistId
              )?.checklistLinkId
            : null
        }
        checklistRecord={
          selectedChecklistId
            ? checklistResponses.find(
                (c) => c.checklistId === selectedChecklistId
              ) ?? null
            : null
        }
        onApprovalComplete={() => {
          if (typeof refreshData === "function") refreshData();
          setShowChecklistViewAndUpdate(false);
          setSelectedChecklistId(null);
        }}
        onClose={() => {
          setShowChecklistViewAndUpdate(false);
          setSelectedChecklistId(null);
        }}
        onSave={(success: boolean) => {
          // Modal handles its own success/error messaging (including the
          // actual API error message on failure). Parent only closes the
          // modal on success and refreshes the list.
          if (success) {
            setShowChecklistViewAndUpdate(false);
            setSelectedChecklistId(null);
            if (typeof refreshData === "function") refreshData();
          }
        }}
      />

      <RejectModal
        isModalVisible={rejectModalVisible}
        setIsModalVisible={setRejectModalVisible}
        onReject={handleRejectConfirm}
        title={`Reject Checklist: ${currentRejectItem?.checklistName || ""}`}
        label="Reason for rejection"
        isLoading={isRejecting}
      />
    </>
  );
};

export default TaskCheckListView;